import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { ArrowLeft, CreditCard, DollarSign, FileText, Clock, CheckCircle, AlertCircle, Plus, Eye, Bell, BarChart3 } from 'lucide-react'
import { toast } from 'react-toastify'
import PaymentProofUpload from '../../components/payment/PaymentProofUpload'
import ReminderPreferences from '../../components/payment/ReminderPreferences'
import PaymentAnalytics from '../../components/payment/PaymentAnalytics'
import ProfessionalInvoiceViewer from '../../components/payment/ProfessionalInvoiceViewer'
import { getUserType } from '../../utils/userPermissions'

interface Invoice {
  id: string
  poId: string
  invoiceNumber: string
  amount: number
  tax: number
  total: number
  currency?: string
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'PENDING' | 'PAYMENT_SENT'
  dueDate: string
  paidDate?: string
  notes?: string
  
  // Payment proof fields
  paymentProofUrl?: string
  paymentProofUploadedAt?: string
  paymentProofUploadedBy?: string
  
  createdAt: string
  updatedAt: string
  po?: {
    id: string
    poNumber: string
    totalAmount: number
    buyer?: {
      companyName?: string
      email?: string
    }
    vendor?: {
      companyName?: string
    }
  }
}

const InvoicingPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showReminderPreferences, setShowReminderPreferences] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [showProfessionalInvoice, setShowProfessionalInvoice] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }
    loadInvoices()
  }, [user, navigate])

  const loadInvoices = async () => {
    try {
      setLoading(true)
      console.log('🔍 Loading invoices for user:', user?.email, 'User ID:', user?.id)
      const response = await apiService.get('/api/post-quote-activity/invoices')
      console.log('🔍 Invoice API response:', response)
      const invoices = (response as any).data || []
      console.log('🔍 Found invoices:', invoices.length, invoices)
      setInvoices(invoices)
    } catch (error: any) {
      console.error('Error loading invoices:', error)
      toast.error(error.response?.data?.message || 'Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800'
      case 'SENT': return 'bg-blue-100 text-blue-800'
      case 'PAYMENT_SENT': return 'bg-yellow-100 text-yellow-800'
      case 'PAID': return 'bg-green-100 text-green-800'
      case 'OVERDUE': return 'bg-red-100 text-red-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DRAFT': return <FileText className="w-4 h-4" />
      case 'SENT': return <Clock className="w-4 h-4" />
      case 'PAYMENT_SENT': return <Clock className="w-4 h-4" />
      case 'PAID': return <CheckCircle className="w-4 h-4" />
      case 'OVERDUE': return <AlertCircle className="w-4 h-4" />
      case 'CANCELLED': return <AlertCircle className="w-4 h-4" />
      default: return <FileText className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }

  const isOverdue = (dueDate: string, status: string) => {
    return status !== 'PAID' && new Date(dueDate) < new Date()
  }

  const updateInvoiceStatus = async (invoiceId: string, status: string) => {
    try {
      await apiService.patch(`/api/post-quote-activity/invoices/${invoiceId}/status`, { status })
      toast.success('Invoice status updated successfully')
      loadInvoices()
    } catch (error: any) {
      console.error('Error updating invoice status:', error)
      toast.error(error.response?.data?.message || 'Failed to update invoice status')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    )
  }

  // Calculate statistics
  const totalInvoices = invoices.length
  const paidInvoices = invoices.filter(inv => inv.status === 'PAID').length
  const overdueInvoices = invoices.filter(inv => isOverdue(inv.dueDate, inv.status)).length
  const totalRevenue = invoices.filter(inv => inv.status === 'PAID').reduce((sum, inv) => sum + inv.total, 0)

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/enterprise/post-quote-activity')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Invoicing & Payment</h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage invoices and track payments</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
              <button
                onClick={() => setShowAnalytics(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm sm:text-base">Analytics</span>
              </button>
              <button
                onClick={() => setShowReminderPreferences(true)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <Bell className="w-4 h-4" />
                <span className="text-sm sm:text-base">Reminder Settings</span>
              </button>
              {user?.role === 'ENTERPRISE_VENDOR' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm sm:text-base">Create Invoice</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{totalInvoices}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid Invoices</p>
                <p className="text-2xl font-bold text-green-600">{paidInvoices}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{overdueInvoices}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-primary-600">{formatCurrency(totalRevenue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-primary-600" />
            </div>
          </div>
        </div>

        {/* Invoices List */}
        {invoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Invoices Yet</h3>
            <p className="text-gray-600 mb-6">
              {user?.role === 'ENTERPRISE_VENDOR' 
                ? 'Start managing your billing by creating your first invoice.'
                : 'No invoices have been created yet for your purchase orders.'
              }
            </p>
            {user?.role === 'ENTERPRISE_VENDOR' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg"
              >
                Create First Invoice
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">All Invoices</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => {
                    try {
                      console.log('🔍 Rendering invoice:', invoice)
                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</p>
                          <p className="text-sm text-gray-500">PO: {invoice.po?.poNumber || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{invoice.po?.buyer?.companyName || 'N/A'}</p>
                          <p className="text-sm text-gray-500">{invoice.po?.buyer?.email || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(invoice.total, invoice.currency)}
                          </p>
                          {invoice.tax > 0 && (
                            <p className="text-sm text-gray-500">
                              +{formatCurrency(invoice.tax, invoice.currency)} tax
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className={`text-sm font-medium ${isOverdue(invoice.dueDate, invoice.status) ? 'text-red-600' : 'text-gray-900'}`}>
                            {formatDate(invoice.dueDate)}
                          </p>
                          {isOverdue(invoice.dueDate, invoice.status) && (
                            <p className="text-sm text-red-500">Overdue</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 w-fit ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          <span>{invoice.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              console.log('🔍 Eye button clicked for invoice:', invoice.id)
                              setSelectedInvoice(invoice)
                              setShowDetailModal(true)
                              console.log('🔍 Modal state set to true')
                            }}
                            className="text-primary-600 hover:text-primary-700"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice)
                              setShowProfessionalInvoice(true)
                            }}
                            className="text-blue-600 hover:text-blue-700"
                            title="Professional PDF"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {(invoice.status === 'PENDING' || invoice.status === 'OVERDUE') && user?.role === 'ENTERPRISE_BUYER' && (
                            <button
                              onClick={() => updateInvoiceStatus(invoice.id, 'PAYMENT_SENT')}
                              className="text-yellow-600 hover:text-yellow-700 text-xs"
                            >
                              Mark Payment Sent
                            </button>
                          )}
                          {invoice.status === 'PAYMENT_SENT' && user?.role === 'ENTERPRISE_VENDOR' && (
                            <button
                              onClick={() => updateInvoiceStatus(invoice.id, 'PAID')}
                              className="text-green-600 hover:text-green-700 text-xs"
                            >
                              Confirm Payment
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                      )
                    } catch (error) {
                      console.error('🔍 Error rendering invoice:', invoice.id, error)
                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td colSpan={6} className="px-6 py-4 text-center text-red-600">
                            Error loading invoice data
                          </td>
                        </tr>
                      )
                    }
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && user?.role === 'ENTERPRISE_VENDOR' && (
        <CreateInvoiceModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadInvoices()
          }}
        />
      )}

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <InvoiceDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedInvoice(null)
          }}
          invoice={selectedInvoice}
          onUpdate={loadInvoices}
          user={user}
        />
      )}

      {/* Reminder Preferences Modal */}
      <ReminderPreferences
        isOpen={showReminderPreferences}
        onClose={() => setShowReminderPreferences(false)}
      />

      {/* Analytics Modal */}
      <PaymentAnalytics
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
      />

      {/* Professional Invoice Viewer Modal */}
      {showProfessionalInvoice && selectedInvoice && (
        <ProfessionalInvoiceViewer
          isOpen={showProfessionalInvoice}
          onClose={() => setShowProfessionalInvoice(false)}
          invoiceId={selectedInvoice.id}
          invoiceNumber={selectedInvoice.invoiceNumber}
          user={user}
        />
      )}
    </div>
  )
}

// Create Invoice Modal Component
const CreateInvoiceModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    poId: '',
    amount: '',
    tax: '0',
    dueDate: '',
    notes: ''
  })
  const [loading, setLoading] = useState(false)
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([])

  useEffect(() => {
    if (isOpen) {
      loadPurchaseOrders()
      // Set default due date to 30 days from now
      const defaultDueDate = new Date()
      defaultDueDate.setDate(defaultDueDate.getDate() + 30)
      setFormData(prev => ({ ...prev, dueDate: defaultDueDate.toISOString().split('T')[0] }))
    }
  }, [isOpen])

  const loadPurchaseOrders = async () => {
    try {
      const response = await apiService.get('/api/post-quote-activity/purchase-orders')
      setPurchaseOrders((response as any).data || [])
    } catch (error) {
      console.error('Error loading purchase orders:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.poId || !formData.amount) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setLoading(true)
      await apiService.post('/api/post-quote-activity/invoices', {
        ...formData,
        amount: parseFloat(formData.amount),
        tax: parseFloat(formData.tax)
      })
      toast.success('Invoice created successfully')
      onSuccess()
    } catch (error: any) {
      console.error('Error creating invoice:', error)
      toast.error(error.response?.data?.message || 'Failed to create invoice')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const baseAmount = parseFloat(formData.amount || '0')
  const taxRate = parseFloat(formData.tax || '0') / 100 // Convert percentage to decimal
  const taxAmount = baseAmount * taxRate
  const total = baseAmount + taxAmount

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Create Invoice</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order *
            </label>
            <select
              value={formData.poId}
              onChange={(e) => {
                const selectedPoId = e.target.value
                const selectedPo = purchaseOrders.find(po => po.id === selectedPoId)
                setFormData({ 
                  ...formData, 
                  poId: selectedPoId,
                  amount: selectedPo ? selectedPo.totalAmount.toString() : ''
                })
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select Purchase Order</option>
              {purchaseOrders.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.poNumber} - {po.buyer.companyName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount * (from Purchase Order)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50"
              placeholder="Select a Purchase Order to auto-populate"
              required
              readOnly={!!formData.poId}
            />
            {formData.poId && (
              <p className="text-xs text-gray-500 mt-1">
                Amount automatically populated from the selected Purchase Order
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax Rate (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.tax}
              onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter tax rate as percentage (e.g., 9 for 9%)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due Date *
            </label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>

          {baseAmount > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Amount:</span>
                  <span className="text-gray-900">${baseAmount.toFixed(2)}</span>
                </div>
                {taxRate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax ({formData.tax}%):</span>
                    <span className="text-gray-900">${taxAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-1 flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Total Amount:</span>
                  <span className="text-lg font-semibold text-gray-900">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Invoice Detail Modal Component
const InvoiceDetailModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  invoice: Invoice
  onUpdate: () => void
  user: any
}> = ({ isOpen, onClose, invoice, user }) => {

  
  // Payment proof upload state
  const [showPaymentProofUpload, setShowPaymentProofUpload] = useState(false)
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800'
      case 'SENT': return 'bg-blue-100 text-blue-800'
      case 'PAYMENT_SENT': return 'bg-yellow-100 text-yellow-800'
      case 'PAID': return 'bg-green-100 text-green-800'
      case 'OVERDUE': return 'bg-red-100 text-red-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Invoice Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Invoice Number</p>
              <p className="font-semibold">{invoice.invoiceNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                {invoice.status}
              </span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Billing Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-semibold">{invoice.po?.buyer?.companyName || 'N/A'}</p>
                <p className="text-sm text-gray-600">{invoice.po?.buyer?.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Purchase Order</p>
                <p className="font-semibold">{invoice.po?.poNumber || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Payment Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Amount:</span>
                <span>${(Number(invoice.amount) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax:</span>
                <span>${(Number(invoice.tax) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-2">
                <span>Total:</span>
                <span>${(Number(invoice.total) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Due Date</p>
              <p className="font-semibold">{new Date(invoice.dueDate).toLocaleDateString()}</p>
            </div>
            {invoice.paidDate && (
              <div>
                <p className="text-sm text-gray-500">Paid Date</p>
                <p className="font-semibold">{new Date(invoice.paidDate).toLocaleDateString()}</p>
              </div>
            )}
          </div>

          {invoice.notes && (
            <div>
              <p className="text-sm text-gray-500">Notes</p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p>{invoice.notes}</p>
              </div>
            </div>
          )}

          {/* Payment Proof Section - Extension */}
          {invoice.paymentProofUrl ? (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-green-900">Payment Proof</h3>
              <div className="space-y-2">
                <p className="text-sm text-green-700">
                  Payment proof uploaded on {new Date(invoice.paymentProofUploadedAt!).toLocaleDateString()}
                </p>
                <a
                  href={invoice.paymentProofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  View Payment Proof
                </a>
              </div>
            </div>
          ) : (() => {
            const userType = getUserType(user)
            const shouldShow = (invoice.status === 'PENDING' || invoice.status === 'OVERDUE' || invoice.status === 'PAYMENT_SENT') && userType === 'ENTERPRISE_BUYER'
            console.log('🔍 Payment proof button check - Status:', invoice.status, 'User type:', userType, 'Should show:', shouldShow)
            return shouldShow
          })() && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-blue-900">Payment Proof</h3>
              <p className="text-sm text-blue-700 mb-3">
                Upload proof of payment to mark this invoice as paid
              </p>
              <button
                onClick={() => setShowPaymentProofUpload(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
              >
                Upload Payment Proof
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
          >
            Close
          </button>
        </div>

        {/* Payment Proof Upload Modal - Extension */}
        <PaymentProofUpload
          isOpen={showPaymentProofUpload}
          onClose={() => setShowPaymentProofUpload(false)}
          invoiceId={invoice.id}
          onUploadSuccess={(proofUrl) => {
            console.log('Payment proof uploaded:', proofUrl)
            // Will be integrated with mark as paid in Phase 1.2
            toast.success('Payment proof uploaded successfully')
            setShowPaymentProofUpload(false)
          }}
        />
      </div>
    </div>
  )

}

export default InvoicingPage